import { EU_COUNTRIES_FR } from "~/utils/countries";

export const useFormValidation = () => {
  const validateEmail = (email: string): string | null => {
    if (!email || email.trim() === "") {
      return "L'adresse email est obligatoire";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return "Format email invalide";
    }
    return null;
  };

  const validateRequired = (
    value: string,
    fieldName: string = "Ce champ",
  ): string | null => {
    if (!value || value.trim() === "") {
      return `${fieldName} est obligatoire`;
    }
    return null;
  };

  const validateBirthDate = (birthDate: string): string | null => {
    if (!birthDate) {
      return "La date de naissance est obligatoire";
    }

    const birthDateObj = new Date(birthDate);
    const today = new Date();

    if (birthDateObj > today) {
      return "La date de naissance ne peut pas être future";
    }

    // Check if person is over 120 years old (basic sanity check)
    const age = today.getFullYear() - birthDateObj.getFullYear();
    if (age > 120) {
      return "Date de naissance invalide";
    }

    return null;
  };

  const validatePassportDates = (
    issueDate: string,
    expiryDate: string,
  ): { issueError?: string; expiryError?: string } => {
    const errors: { issueError?: string; expiryError?: string } = {};

    if (!issueDate) {
      errors.issueError = "La date de création est obligatoire";
    }

    if (!expiryDate) {
      errors.expiryError = "La date de validité est obligatoire";
    }

    if (issueDate && expiryDate) {
      const issueDateObj = new Date(issueDate);
      const expiryDateObj = new Date(expiryDate);
      const today = new Date();
      const minValidityDate = new Date(today);
      minValidityDate.setMonth(today.getMonth() + 6); // Passport should be valid for at least 6 months

      if (issueDateObj >= expiryDateObj) {
        errors.issueError =
          "La date de création doit être antérieure à la date d'expiration";
      }

      if (expiryDateObj < minValidityDate) {
        errors.expiryError = "Le passeport doit être valide au moins 6 mois";
      }
    }

    return errors;
  };

  const validateDocumentFile = (
    file: File | null,
    required: boolean = true,
  ): string | null => {
    if (required && !file) {
      return "Ce document est obligatoire";
    }

    if (file) {
      // Check file size (max 10MB)
      const maxSizeBytes = 10 * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        return "Le fichier ne peut pas dépasser 10MB";
      }

      // Check file type
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "application/pdf",
      ];
      if (!allowedTypes.includes(file.type)) {
        return "Type de fichier non supporté. Utilisez JPG, PNG, GIF ou PDF";
      }
    }

    return null;
  };

  // Validate passport number format (required is handled separately)
  const validatePassportNumberFormat = (num: string): string | null => {
    if (!num || num.trim() === "") return null;
    const cleaned = num.replace(/\s|-/g, "");
    // Universal rule: total length 6–12.
    // Allowed patterns:
    // - Only digits
    // - 1–2 letters at the beginning, rest digits
    // - 1–2 letters at the end, rest digits
    const regex =
      /^(?:\d{6,12}|[A-Za-z]\d{5,11}|[A-Za-z]{2}\d{4,10}|\d{5,11}[A-ZaZ]|\d{4,10}[A-Za-z]{2})$/;
    if (!regex.test(cleaned)) {
      return "Numéro de passeport invalide (6 à 12 caractères : uniquement chiffres, ou 1 à 2 lettres au début/fin et le reste en chiffres)";
    }
    return null;
  };

  // Country-aware validator, falls back to universal rule

  const validateTravelerForm = (
    traveler: any,
    isPrimary: boolean = true,
  ): Record<string, string> => {
    const errors: Record<string, string> = {};

    // Determine if this is an adult traveler
    const isAdult = traveler.type === "adult";

    // Basic required fields for ALL travelers (adults, children, babies)
    const basicRequiredFields = [
      { key: "firstName", name: "Prénom" },
      { key: "lastName", name: "Nom" },
    ];

    basicRequiredFields.forEach((field) => {
      const error = validateRequired(traveler[field.key], field.name);
      if (error) errors[field.key] = error;
    });

    // Additional required fields ONLY for adults
    if (isAdult) {
      const adultOnlyFields = [
        { key: "country", name: "Pays" },
        { key: "postalAddress", name: "Adresse postale" },
        { key: "city", name: "Ville" },
        { key: "postalCode", name: "Code postal" },
      ];

      adultOnlyFields.forEach((field) => {
        const error = validateRequired(traveler[field.key], field.name);
        if (error) errors[field.key] = error;
      });

      // Postal code numeric validation - ONLY for adults
      if (traveler.postalCode && !/^\d+$/.test(traveler.postalCode)) {
        errors.postalCode =
          "Le code postal doit contenir uniquement des chiffres";
      }

      // Email validation - ONLY for adults
      // Required for the primary traveler; optional for companions (they may share
      // the primary's email or simply not have their own).
      if (isPrimary) {
        const emailError = validateEmail(traveler.email);
        if (emailError) errors.email = emailError;
      } else if (traveler.email && traveler.email.trim() !== "") {
        // Companion provided an email — validate format only
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(traveler.email.trim())) {
          errors.email = "Format email invalide";
        }
      }

      // Phone number validation - ONLY for adults
      if (!traveler.phoneNumber || traveler.phoneNumber.trim() === "") {
        errors.phoneNumber = "Numéro de téléphone est obligatoire";
      }
    }

    // Birth date validation - for ALL travelers
    const birthDateError = validateBirthDate(traveler.birthDate);
    if (birthDateError) errors.birthDate = birthDateError;

    // NOTE: Document validation removed - documents are now uploaded in Step 4
    // No need to validate passport, CIN, or visa files here
    // The form only collects traveler information and document requirements (flags)

    return errors;
  };

  return {
    validateEmail,
    validateRequired,
    validateBirthDate,
    validatePassportDates,
    validateDocumentFile,
    validatePassportNumberFormat,
    validateTravelerForm,
  };
};
